import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Button,
  Stack,
  useTheme,
  alpha,
  Paper,
  Fade,
  Popover,
} from '@mui/material';
import { useSubjectLanguage, useUserState, useOnboardingSteps } from '@features/user';
import { useModal } from '@store/hooks';
import { MODAL_TYPES } from '../../../components/modals';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import SchedulableItem from './SchedulableItem';
import SchedulingForm from './SchedulingForm';
import { EXPANDABLE_ITEM_HEIGHT } from '../constants';
import { useExpandableField } from '../store';

// Define TypeScript interfaces for the component props
export interface ExpandableFieldItem {
  id: string | number;
  name: string;
  status?: string;
  timing?: string; // Human-readable timing display
  offset?: string; // ISO 8601 duration format
  formId?: string; // For forms, this stores the actual form_id from the API
  scheduledFor?: string;
  sentAt?: string;
  completedAt?: string;
  ruleId?: string; // Links to scheduled action via rule_id
  scheduledActionStatus?: string; // The actual status from scheduled action
  errorMessage?: string; // Error message for failed actions
  executedAt?: string; // When the action was executed
  scheduledAt?: string; // When the action is scheduled to execute
}

export interface SchedulingData {
  name: string;
  timing: string;
  customTiming: string; // ISO 8601 duration format - now always provided
  formId?: string; // For forms, this will be the form_id from the API
}

export interface ExpandableFieldProps {
  title: string;
  items: ExpandableFieldItem[];
  onAddItem: (item: SchedulingData) => void;
  onUpdateItem: (itemId: string | number, item: SchedulingData) => void;
  onDeleteItem: (itemId: string | number) => void;
  isFormType?: boolean;
  mode?: 'view' | 'create' | 'edit';
  // Disabled state props
  disabled?: boolean;
  disabledText?: string;
  onEditPatientInfo?: (event: React.MouseEvent<HTMLElement>) => void;
}

const ExpandableField: React.FC<ExpandableFieldProps> = ({
  title,
  items,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  isFormType = false,
  mode = 'view',
  disabled = false,
  disabledText,
  onEditPatientInfo,
}) => {
  const subjectLanguage = useSubjectLanguage();
  const userState = useUserState();
  const onboardingSteps = useOnboardingSteps();
  const { open: openModal } = useModal();

  // Generate a unique field ID for this expandable field
  const fieldId = `${title}-${isFormType ? 'forms' : 'reminders'}`;
  const { expanded, setExpanded } = useExpandableField(fieldId);

  // Force collapsed when disabled
  const isExpanded = disabled ? false : expanded;

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editingItem, setEditingItem] = useState<ExpandableFieldItem | null>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Determine if the label should be in the "floated" position
  const shouldFloatLabel = items.length > 0 || isExpanded || (disabled && disabledText);

  // Auto-open scheduling form when expanded with no items -- only on load
  useEffect(() => {
    if (isExpanded && items.length === 0 && !formMode && !disabled) {
      setFormMode('create');
    }
  }, [isExpanded, disabled]);

  // Clean up: close the expandable field on unmount
  useEffect(() => {
    return () => {
      setExpanded(false);
    };
  }, []);

  const getSummary = (): string => {
    // Return empty string when no items to allow label to take the space
    if (items.length === 0) {
      return '';
    }

    if (mode === 'create') {
      const count = items.length;
      return `${count} ${title.toLowerCase()} configured`;
    }

    if (mode === 'edit') {
      const count = items.length;
      return `${count} ${title.toLowerCase()} configured`;
    }

    // Default 'view' mode
    const counts = items.reduce((acc: Record<string, number>, item: ExpandableFieldItem) => {
      const status = item.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([status, count]) => `${count} ${status}`)
      .join(', ');
  };

  const getPeekItems = () => {
    if (items.length === 0) {
      return [];
    }

    // Return items with formatted status for popover display
    return items.map((item: ExpandableFieldItem) => {
      let status: string;
      switch (item.status) {
        case 'scheduled':
          status = `scheduled`;
          break;
        case 'sending':
          status = 'sending';
          break;
        case 'sent':
          status = 'sent';
          break;
        case 'failed':
          status = 'failed';
          break;
        case 'cancelled':
          status = 'canceled';
          break;
        default:
          status = item.timing || 'configured';
      }
      return {
        name: item.name,
        status: status,
      };
    });
  };

  const getDisplayText = (): string => {
    if (disabled && disabledText) {
      return disabledText;
    }
    return getSummary();
  };

  const getActionText = (): string => {
    if (disabled) {
      // Only show action text if there's a way to edit patient info
      return onEditPatientInfo ? `update ${subjectLanguage} info` : '';
    }
    switch (mode) {
      case 'create':
        return 'set up?';
      case 'edit':
        return 'modify?';
      default:
        return 'see more?';
    }
  };

  const handleToggleExpanded = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      // Don't expand when disabled, trigger edit patient info instead
      if (onEditPatientInfo) {
        onEditPatientInfo(event);
      }
      return;
    }

    // Check if the onboarding step is completed
    if (!onboardingSteps?.patient_communication_sms_onboarding_completed) {
      openModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {
        continuationAction: {
          type: 'appointmentTypes/setExpandableFieldState',
          payload: {
            fieldId,
            expanded: !isExpanded,
          },
        },
      });
      return;
    }

    // Update Redux state
    setExpanded(!isExpanded);
  };

  const handleEdit = (item: ExpandableFieldItem) => {
    setEditingItem(item);
    setFormMode('edit');
  };

  const handleFormSubmit = (data: SchedulingData) => {
    if (formMode === 'edit' && editingItem) {
      onUpdateItem(editingItem.id, data);
    } else {
      onAddItem(data);
    }
    // Reset form state
    setFormMode(null);
    setEditingItem(null);
  };

  const handleFormCancel = () => {
    // Simply reset form state - animation will handle the transition
    setFormMode(null);
    setEditingItem(null);
  };

  const handleAddNew = () => {
    setFormMode('create');
    setEditingItem(null);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* TextField-style outlined container that grows to wrap expanded content */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          borderRadius: 1,
          border: `1px solid rgba(0, 0, 0, 0.23)`,
          '&:hover': {
            borderColor: theme.palette.text.primary,
          },
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
          },
          transition: 'border-color 0.2s ease, border-width 0.1s ease',
          cursor: isExpanded ? undefined : disabled && !onEditPatientInfo ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          backgroundColor: 'background.paper',
        }}
      >
        {/* Floating label that behaves like MUI TextField */}
        <Typography
          variant="body2"
          component="label"
          sx={{
            position: 'absolute',
            top: shouldFloatLabel ? -8 : '50%',
            left: 12,
            transform: shouldFloatLabel ? 'translateY(0)' : 'translateY(-50%)',
            backgroundColor: 'background.paper',
            px: shouldFloatLabel ? 0.3 : 0,
            color: isExpanded ? 'primary.main' : 'text.secondary',
            fontSize: shouldFloatLabel ? '0.75rem' : '1rem',
            transition: 'all 0.2s cubic-bezier(0.0, 0, 0.2, 1)',
            zIndex: 2,
            pointerEvents: 'none',
            // Background for floated label
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: -4,
              right: -4,
              bottom: 0,
              backgroundColor: 'background.paper',
              zIndex: -1,
              opacity: shouldFloatLabel ? 1 : 0,
              transition: 'opacity 0.2s cubic-bezier(0.0, 0, 0.2, 1)',
            },
          }}
        >
          {title}
        </Typography>

        {/* Collapsed field content */}
        <Box
          sx={{
            p: '11px 14px', // Match standard MUI TextField padding
            minHeight: '1.4375em', // Match standard MUI TextField height
            display: 'flex',
            alignItems: 'center',
            cursor: disabled && !onEditPatientInfo ? 'default' : 'pointer',
          }}
          onClick={handleToggleExpanded}
          onMouseEnter={event => {
            if (!isExpanded && items.length > 0 && !disabled) {
              setPopoverAnchorEl(event.currentTarget);
            }
          }}
          onMouseLeave={() => {
            setPopoverAnchorEl(null);
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box flex={1}>
              <Collapse in={!isExpanded}>
                {(items.length > 0 || disabled) && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontSize: disabled ? '0.875rem' : '1rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {getDisplayText()}
                  </Typography>
                )}
              </Collapse>
            </Box>
            <Box display="flex" alignItems="center" gap={1} ml={2}>
              <Collapse in={!isExpanded} orientation="horizontal">
                {(items.length > 0 || (disabled && onEditPatientInfo)) && getActionText() && (
                  <Typography variant="body2" color="primary.main" sx={{ whiteSpace: 'nowrap' }}>
                    {getActionText()}
                  </Typography>
                )}
              </Collapse>
              <IconButton
                size="small"
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  color: disabled && onEditPatientInfo ? 'primary.main' : 'text.secondary',
                }}
              >
                {disabled && onEditPatientInfo ? <EditOutlinedIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Peek Summary Popover */}
        <Popover
          open={Boolean(popoverAnchorEl) && !isExpanded && items.length > 0}
          anchorEl={popoverAnchorEl}
          onClose={() => setPopoverAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableRestoreFocus
          disableScrollLock
          marginThreshold={16}
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              pointerEvents: 'none',
              mt: 1,
              maxWidth: '90vw',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                alignItems: 'center',
                rowGap: 1,
              }}
            >
              {getPeekItems().map((item, index) => (
                <React.Fragment key={index}>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {item.status}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Popover>

        {/* Expanded Content - now inside the same border container */}
        <Collapse in={isExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Items List */}
            <Stack spacing={1} sx={{ mb: 2 }}>
              {items.map(item => (
                <React.Fragment key={item.id}>
                  {formMode !== 'edit' || editingItem?.id !== item.id ? (
                    <SchedulableItem
                      item={item}
                      onEdit={handleEdit}
                      onDelete={onDeleteItem}
                      isFormType={isFormType}
                    />
                  ) : (
                    <SchedulingForm
                      mode="edit"
                      initialData={editingItem || undefined}
                      isFormType={isFormType}
                      onSubmit={handleFormSubmit}
                      onCancel={handleFormCancel}
                    />
                  )}
                </React.Fragment>
              ))}
            </Stack>

            {/* Add/Schedule Button or Form with Animation */}
            {formMode !== 'create' ? (
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                sx={{
                  borderStyle: 'dashed',
                  height: EXPANDABLE_ITEM_HEIGHT,
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': {
                    borderStyle: 'dashed',
                    borderColor: 'text.secondary',
                    backgroundColor: alpha(theme.palette.action.hover, 0.04),
                  },
                }}
              >
                {mode === 'create'
                  ? `Schedule a ${title.slice(0, -1).toLowerCase()}?`
                  : mode === 'edit'
                    ? `Add another ${title.slice(0, -1).toLowerCase()}?`
                    : `Schedule a ${title.slice(0, -1).toLowerCase()}?`}
              </Button>
            ) : (
              <SchedulingForm
                mode="create"
                isFormType={isFormType}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default ExpandableField;
